/**
 * @callback AppletBase~isAvailable
 *
 * @returns {Promise<boolean>} - True if the applet is available, false otherwise
 */

const MINIMUM_TAG_NAME_LENGTH = 3;

/**
 *  Validate a string is a valid applet tag name.
 *  Tag names must be
 *  - all lowercase characters or hyphen ("-")
 *  - minimum of 3 characters long
 *  - contain at least 1 hyphen ("-")
 *
 * @param {string} tagName - tagName to validate
 * @returns {boolean} - True is valid, false if not
 */
export function isValidAppletTagName(tagName) {
    const tagNameRegex = new RegExp(/^[a-z][a-z0-9]*-[a-z0-9-]+$/);

    if (typeof tagName !== 'string') {
        return false;
    }

    if (tagName.length < MINIMUM_TAG_NAME_LENGTH) {
        return false;
    }

    return tagNameRegex.test(tagName);
}

/**
 * @param {object} options - SEE BELOW
 * @param {string} options.tagName - The name of the tag that will be used to render the applet in the dom. Minimum length of 3 with only alpha and hyphen characters allowed.
 * @param {AppletBase~isAvailable} options.isAvailable - A callback to let us know if the applet should be available
 */
export class AppletBase {
    constructor({ tagName, isAvailable, ...options }) {
        if (!isValidAppletTagName(tagName)) {
            throw new Error(`Only alpha and hyphen characters are allowed and it must be at least 3 characters long. Provided value: ${tagName}`);
        }

        if(typeof isAvailable !== 'function') {
            throw new TypeError(`isAvailable must be a function. Provided value: ${isAvailable}`);
        }

        Object.assign(this, {
            tagName,
            isAvailable,
            ...options
        });
        Object.freeze(this);
    }
}
